/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { palDataHookStore } from '../useStore/useStore.js';
import generateGQLSchema from '../utils/generateGQLSchema.js';
import getGraphqlData from '../utils/getGraphqlData.js';
import request from '../utils/request.js';
import getQueryParams from '../utils/getQueryParams.js';

function setPollingInterval(_ref) {
  var key = _ref.key,
    polling = _ref.polling,
    setData = _ref.setData;
  if (typeof polling === 'number') {
    var pollingIntervalId = setTimeout(function () {
      // Allow requests on this hook again
      palDataHookStore[key].setMakeRequest("polling-".concat(new Date()));

      // Set the state to loading
      setData({
        loading: true,
        aborted: false
      });
    }, polling);
    palDataHookStore[key].setPollingInterval(pollingIntervalId);
  }
}
var retrieve = function retrieve(_ref2) {
  var key = _ref2.key,
    setData = _ref2.setData,
    params = _ref2.params,
    _ref2$options = _ref2.options,
    options = _ref2$options === void 0 ? {
      mock: false
    } : _ref2$options,
    method = _ref2.method,
    defaultMockResponse = _ref2.mock,
    uri = _ref2.uri,
    headers = _ref2.headers,
    enableImsAccountHeader = _ref2.enableImsAccountHeader,
    enableBssAccountHeader = _ref2.enableBssAccountHeader,
    graphql = _ref2.graphql,
    _ref2$dataHelper = _ref2.dataHelper,
    dataHelper = _ref2$dataHelper === void 0 ? function (data) {
      return data;
    } : _ref2$dataHelper;
  var mock = options.mock,
    mockResponse = options.mockResponse,
    queryParams = options.queryParams,
    polling = options.polling,
    customSchema = options.customSchema,
    callback = options.callback,
    _options$customDataHe = options.customDataHelper,
    customDataHelper = _options$customDataHe === void 0 ? function (data) {
      return data;
    } : _options$customDataHe,
    _options$customMockOb = options.customMockObject,
    customMockObject = _options$customMockOb === void 0 ? {} : _options$customMockOb,
    optionsHeaders = options.headers;
  var transformData = function transformData(data) {
    return customDataHelper(dataHelper(data));
  };

  // If the key provided is skip, skip the API call
  if (key === 'skip' || !palDataHookStore[key].makeRequest) {
    return Promise.resolve();
  }

  // New request is happening so clear any queued up polling
  clearTimeout(palDataHookStore[key].pollingInterval);
  var isPolling = /^polling-/.test(palDataHookStore[key].makeRequest);

  // Set makeRequest to false to prevent other requests from being sent off
  palDataHookStore[key].setMakeRequest(false);
  if (mock) {
    setData(_objectSpread2({
      loading: false,
      error: null,
      data: mockResponse || defaultMockResponse,
      status: 200,
      updated: Date.now()
    }, customMockObject));
    if (callback) {
      callback(mockResponse || defaultMockResponse);
    }
  } else {
    var uriWithQueryParams = "".concat(uri).concat(getQueryParams(queryParams, true));
    var controller = typeof AbortController !== 'undefined' && new AbortController();
    var signal = controller && controller.signal || undefined;
    palDataHookStore[key].setAbortController(controller);
    var fetchOptions = {
      method: method,
      headers: _objectSpread2(_objectSpread2({}, headers), optionsHeaders),
      enableImsAccountHeader: enableImsAccountHeader,
      enableBssAccountHeader: enableBssAccountHeader,
      isPolling: isPolling,
      credentials: 'include',
      signal: signal
    };
    if (method !== 'GET') {
      fetchOptions.body = JSON.stringify(graphql // If a graphql is provided then generate a graphql request body
      ? {
        query: generateGQLSchema(graphql, customSchema),
        variables: params
      } : params);
    }

    // Make a fetch call based on the props
    return request(uriWithQueryParams, fetchOptions).then(function (response) {
      // Get status from the response to be returned with the data/error
      var status = response.status;
      var loadResponse = response.text();
      loadResponse.then(function (rawbody) {
        var body = rawbody;
        if (rawbody.length) {
          try {
            body = JSON.parse(rawbody);
            // eslint-disable-next-line no-empty
          } catch (error) {}
        }
        if (status >= 400) {
          // If non graphql and an error status code set the body as the error object
          setData({
            loading: false,
            data: null,
            error: body || {
              status: status
            },
            status: status
          });
        } else if (graphql) {
          var data = transformData(getGraphqlData(body, graphql) || null);
          var error = body.errors && body.errors[0] || null;
          var _body = body,
            _body$errors = _body.errors,
            errors = _body$errors === void 0 ? null : _body$errors;
          // If the call is a grahpql call render data from the graphql response
          var newState = {
            loading: false,
            data: data,
            error: error,
            errors: errors,
            status: status
          };
          if (data && !error) {
            newState.updated = Date.now();
          }
          setData(newState);

          // Executes the callback code if provided
          if (callback && data && !error) {
            callback(data);
          }
        } else {
          var _data = transformData(body);
          // If a non graphql body check status codes for success
          setData({
            loading: false,
            data: _data,
            error: null,
            status: status,
            updated: Date.now()
          });

          // return the body in a callback if it exists and the request succeeds
          if (callback) {
            callback(_data);
          }
        }
        setPollingInterval({
          key: key,
          polling: polling,
          setData: setData
        });
      })["catch"](function (error // If any error return a 500 server error with the error message
      ) {
        // Print error to console
        console.error(error);
        setData({
          loading: false,
          data: null,
          error: error,
          status: 500
        });
        setPollingInterval({
          key: key,
          polling: polling,
          setData: setData
        });
      });
    })["catch"](function (error) {
      // Print error to console
      console.error(error);
      setData({
        loading: false,
        data: null,
        error: error,
        status: null
      });
      setPollingInterval({
        key: key,
        polling: polling,
        setData: setData
      });
    });
  }
  return Promise.resolve();
};
var retrieve$1 = retrieve;

export { retrieve$1 as default };
